<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useNotificationSettingsStore } from '../stores/notificationSettingsStore'
import NotificationMatrixTable from '../components/settings/NotificationMatrixTable.vue'
import NotificationSoundPanel from '../components/settings/NotificationSoundPanel.vue'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePushSubscription } from '@/composables/usePushSubscription'

/**
 * Сохранение лежит внутри каждой панели (NotificationMatrixTable и NotificationSoundPanel) —
 * у каждой свой источник данных (бек-настройки vs локальный стор звука).
 */

const tab = ref<'notifications' | 'sound'>('notifications')

const { mobile } = useDisplay()
const { isSupported, isSubscribed, permission, canSubscribe, subscribe, unsubscribe, checkSubscription } = usePushSubscription()

const settingsStore = useNotificationSettingsStore()
const { isLoading } = storeToRefs(settingsStore)
const feedbackStore = useFeedbackStore()

const handleSubscribe = async () => {
  const success = await subscribe()
  if (success) {
    feedbackStore.showToast({
      type: 'success',
      message: 'Вы успешно подписались на пуш-уведомления!',
      time: 4000,
    })
  } else {
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось подписаться на уведомления. Проверьте разрешения в браузере.',
      time: 5000,
    })
  }
}

const handleUnsubscribe = async () => {
  const success = await unsubscribe()
  if (success) {
    feedbackStore.showToast({
      type: 'info',
      message: 'Пуш-уведомления отключены.',
      time: 4000,
    })
  } else {
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось отключить пуш-уведомления.',
      time: 5000,
    })
  }
}

watch([mobile, isSupported, isSubscribed, permission, canSubscribe], () => {
  console.log('[Push Debug] Состояние PWA на устройстве:', {
    mobile: mobile.value,
    isSupported: isSupported.value,
    isSubscribed: isSubscribed.value,
    permission: permission.value,
    canSubscribe: canSubscribe.value,
    detailedSupport: {
      serviceWorker: 'serviceWorker' in navigator,
      PushManager: 'PushManager' in window,
      Notification: 'Notification' in window,
      hasVapidKey: !!import.meta.env.VITE_WEBPUSH_PUBLIC_KEY,
    },
  })
}, { immediate: true })

onMounted(async () => {
  await settingsStore.fetchSettings()
  await checkSubscription()
})
</script>

<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex ga-1">
      <VBtn
        :color="tab === 'notifications' ? 'primary' : 'secondary'"
        :variant="tab === 'notifications' ? 'flat' : 'text'"
        @click="tab = 'notifications'"
      >
        <template #prepend>
          <VIcon
            icon="ri-notification-2-line"
            :color="tab === 'notifications' ? 'white' : 'high-emphasis'"
          />
        </template>
        Уведомления
      </VBtn>
      <VBtn
        :color="tab === 'sound' ? 'primary' : 'secondary'"
        :variant="tab === 'sound' ? 'flat' : 'text'"
        @click="tab = 'sound'"
      >
        <template #prepend>
          <VIcon
            icon="ri-volume-up-line"
            :color="tab === 'sound' ? 'white' : 'high-emphasis'"
          />
        </template>
        Звук
      </VBtn>
    </div>

    <VWindow v-model="tab" :touch="false">
      <VWindowItem value="notifications">
        <VProgressLinear
          v-if="isLoading"
          indeterminate
          color="primary"
        />
        <div v-else class="d-flex flex-column ga-4">
          <!-- Карточка управления Push-уведомлениями для мобильных PWA -->
          <VCard v-if="mobile && isSupported" class="pa-4 border">
            <div class="d-flex align-center justify-space-between flex-wrap ga-3">
              <div class="d-flex flex-column ga-1">
                <div class="text-subtitle-1 font-weight-bold d-flex align-center ga-2">
                  <VIcon icon="ri-notification-3-line" color="primary" />
                  Уведомления на телефоне (PWA)
                </div>
                <div class="text-caption text-medium-emphasis">
                  Получайте мгновенные пуш-уведомления о новых событиях на вашем телефоне
                </div>
              </div>

              <div v-if="!isSubscribed" class="w-100 d-flex flex-column ga-1">
                <VBtn
                  block
                  color="primary"
                  prepend-icon="ri-notification-badge-line"
                  @click="handleSubscribe"
                >
                  Подписаться
                </VBtn>
                <div v-if="permission === 'denied'" class="d-flex align-center justify-center ga-1 text-error">
                  <VIcon icon="ri-error-warning-line" size="16" />
                  <span class="text-caption font-weight-medium">Уведомления заблокированы в настройках браузера</span>
                </div>
              </div>

              <div v-else-if="isSubscribed" class="w-100 d-flex flex-column ga-2">
                <div class="d-flex align-center justify-center ga-1 text-success">
                  <VIcon icon="ri-checkbox-circle-line" />
                  <span class="text-body-2 font-weight-medium">Уведомления включены</span>
                </div>
                <VBtn
                  block
                  color="secondary"
                  variant="outlined"
                  prepend-icon="ri-notification-off-line"
                  @click="handleUnsubscribe"
                >
                  Отписаться
                </VBtn>
              </div>
            </div>
          </VCard>

          <NotificationMatrixTable />
        </div>
      </VWindowItem>

      <VWindowItem value="sound">
        <NotificationSoundPanel />
      </VWindowItem>
    </VWindow>
  </div>
</template>
